import { useEffect, useState } from "react";
import { Button, Group, Modal, NumberInput, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import HttpClient from "../../helpers/HttpClient";
import { MOVEMENT_TYPE_LABELS, StockMovementType, VariantStock } from "../../types/inventory.type";

// Manual stock bookings: add (initial/purchase), reduce (damaged/lost) or correct.
const MANUAL_TYPES: StockMovementType[] = ["purchase", "initial", "damaged", "lost", "correction"];

interface Props {
  variant: VariantStock | null;
  articleName: string;
  onClose: () => void;
  onSaved: () => void;
}

export function StockMovementDialog({ variant, articleName, onClose, onSaved }: Props) {
  const [type, setType] = useState<StockMovementType>("purchase");
  const [quantity, setQuantity] = useState<number | string>(1);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!variant) return;
    setType(variant.stock === 0 ? "initial" : "purchase");
    setQuantity(1);
    setNote("");
  }, [variant]);

  const reduces = type === "damaged" || type === "lost";
  const qty = Number(quantity) || 0;
  const delta = type === "correction" ? qty : reduces ? -Math.abs(qty) : Math.abs(qty);

  const save = async () => {
    if (!variant || !qty) return;
    setSaving(true);
    try {
      await new HttpClient(`/inventory/variant/${variant.id}`).postAction("movement", {
        type,
        quantity: qty,
        note: note.trim() || undefined,
      });
      notifications.show({ color: "green", message: "Bestand gebucht." });
      onSaved();
      onClose();
    } catch {
      notifications.show({ color: "red", message: "Buchung fehlgeschlagen." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={variant !== null} onClose={onClose} title={`Bestand buchen – ${articleName}`} size="md">
      {variant && (
        <Stack>
          <Text size="sm" c="dimmed">
            Variante: <b>{variant.label}</b> · aktueller Bestand: <b>{variant.stock}</b>
          </Text>
          <SegmentedControl
            fullWidth
            value={type}
            onChange={(v) => setType(v as StockMovementType)}
            data={MANUAL_TYPES.map((t) => ({ value: t, label: MOVEMENT_TYPE_LABELS[t] }))}
          />
          <NumberInput
            label={type === "correction" ? "Menge (+ / −)" : "Menge"}
            description={
              type === "correction"
                ? "Positive Werte erhöhen, negative verringern den Bestand."
                : reduces
                  ? "Wird vom Bestand abgezogen."
                  : "Wird zum Bestand addiert."
            }
            value={quantity}
            onChange={setQuantity}
            allowDecimal={false}
            min={type === "correction" ? undefined : 1}
          />
          <TextInput
            label="Notiz"
            placeholder={reduces ? "z.B. Naht gerissen, beim Auswärtsspiel vergessen" : "z.B. Lieferung Intersport"}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Text size="sm">
            Neuer Bestand: <b>{variant.stock + delta}</b>
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Abbrechen</Button>
            <Button onClick={save} loading={saving} disabled={!qty} color={reduces ? "red" : undefined}>
              Buchen
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
